import http from "@/api/request";
import { DEVICE_OPS_BASE, SHOP_SERVICE_BASE } from "@/config/apiBaseUrlConfig";

import type {
  AgentBatchIdsRequest,
  AgentShopCountItemVO,
  BrandTagCountItemVO,
  BrandTagCountShopsRequest,
  OpsDevicePageQueryRequest,
  PageDataOpsDevicePageVO,
  PageDataShopPageItemVO,
  ShopContractUpdateRequest,
  ShopCreateRequest,
  ShopDetailVO,
  ShopPageQueryRequest,
  ShopPreRegisterRequest,
  ShopStatusUpdateRequest,
  ShopUpdateRequest,
} from "./type";

/**
 * 分页查询门店列表
 * GET /service-chijing-shop/api/shop/getPage
 * @param params 分页与筛选条件
 */
export const getShopPage = (params?: ShopPageQueryRequest) => {
  return http.get<PageDataShopPageItemVO>(`${SHOP_SERVICE_BASE}/getPage`, { params });
};

/**
 * 查询门店详情
 * GET /service-chijing-shop/api/shop/getShop/{id}
 * @param id 门店 ID
 */
export const getShopDetail = (id: string) => {
  return http.get<ShopDetailVO>(`${SHOP_SERVICE_BASE}/getShop/${id}`);
};

/**
 * 新增门店
 * POST /service-chijing-shop/api/shop/addShop
 * @param data 新增参数
 * @returns 门店 ID
 */
export const addShop = (data: ShopCreateRequest) => {
  return http.post<string>(`${SHOP_SERVICE_BASE}/addShop`, data);
};

/**
 * 预注册未激活门店（邀请流程）
 * POST /service-chijing-shop/api/shop/preRegisterShop
 * @param data 预注册参数
 * @returns 门店 ID
 */
export const preRegisterShop = (data: ShopPreRegisterRequest) => {
  return http.post<string>(`${SHOP_SERVICE_BASE}/preRegisterShop`, data);
};

/**
 * 更新门店（不含合同授权；停业门店拒绝更新）
 * PUT /service-chijing-shop/api/shop/updateShop/{id}
 * @param id 门店 ID
 * @param data 更新参数
 */
export const updateShop = (id: string, data: ShopUpdateRequest) => {
  return http.put<ShopDetailVO>(`${SHOP_SERVICE_BASE}/updateShop/${id}`, data);
};

/**
 * 更新门店合同授权
 * PUT /service-chijing-shop/api/shop/updateShopContract/{id}
 * @param id 门店 ID
 * @param data 合同与授权参数
 */
export const updateShopContract = (id: string, data: ShopContractUpdateRequest) => {
  return http.put<ShopDetailVO>(`${SHOP_SERVICE_BASE}/updateShopContract/${id}`, data);
};

/**
 * 切换门店启用/停用状态，并同步组织节点状态
 * PUT /service-chijing-shop/api/shop/updateShopStatus/{id}
 * @param id 门店 ID
 * @param data 状态变更参数
 */
export const updateShopStatus = (id: string, data: ShopStatusUpdateRequest) => {
  return http.put<void>(`${SHOP_SERVICE_BASE}/updateShopStatus/${id}`, data);
};

/**
 * 删除门店
 * DELETE /service-chijing-shop/api/shop/deleteShop/{id}
 * @param id 门店 ID
 */
export const deleteShop = (id: string) => {
  return http.delete<void>(`${SHOP_SERVICE_BASE}/deleteShop/${id}`);
};

/**
 * 按品牌标签 ID 批量统计关联门店数
 * POST /service-chijing-shop/api/shop/countStores
 * @param data 品牌标签 ID 列表
 */
export const countStoresByBrandTags = (data: BrandTagCountShopsRequest) => {
  return http.post<BrandTagCountItemVO[]>(`${SHOP_SERVICE_BASE}/countStores`, data);
};

/**
 * 批量统计代理下辖门店数
 * POST /service-chijing-shop/api/shop/countByAgentIds
 * @param data 代理 ID 列表
 */
export const countShopsByAgentIds = (data: AgentBatchIdsRequest) => {
  return http.post<AgentShopCountItemVO[]>(`${SHOP_SERVICE_BASE}/countByAgentIds`, data);
};

/**
 * 统计工商实体门店关联数
 * GET /service-chijing-shop/api/shop/countByBusinessEntityId/{businessEntityId}
 * @param businessEntityId 工商实体 ID
 * @returns 关联门店数量
 */
export const countShopsByBusinessEntityId = (businessEntityId: string) => {
  return http.get<number>(`${SHOP_SERVICE_BASE}/countByBusinessEntityId/${businessEntityId}`);
};

/**
 * 门店绑定 GAMING_PC 分页
 * GET /service-chijing-device/api/ops/device/getPage
 * @param params shopId 必填；可选 deviceStatus、keyword、分页
 */
export const getDevicePage = (params: OpsDevicePageQueryRequest) => {
  return http.get<PageDataOpsDevicePageVO>(`${DEVICE_OPS_BASE}/getPage`, { params });
};
